import { useState } from "react";
import {
  DollarSign,
  AlertTriangle,
  Users,
  TrendingDown,
  MoreVertical,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { StatCard } from "@/components/admin/StatCard";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import { DataTable } from "@/components/admin/DataTable";
import type { DataTableColumn } from "@/components/admin/DataTable";
import {
  getSubscriptions,
  getDashboardStats,
} from "@/lib/admin/queries";
import {
  extendTrial,
  cancelSubscription,
  upgradeSubscription,
  markAsPaid,
  refundPayment,
} from "@/lib/admin/actions";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";

const PLAN_LABELS: Record<string, string> = {
  individual: "Individual",
  strata: "Strata Council",
  property_manager: "Property Manager",
};

const PLAN_PRICES: Record<string, number> = {
  individual: 9.99,
  strata: 18.99,
  property_manager: 28.99,
};

function getPlanKey(priceId: string): string {
  const lower = (priceId ?? "").toLowerCase();
  if (lower.includes("property_manager") || lower.includes("property-manager")) return "property_manager";
  if (lower.includes("strata")) return "strata";
  if (lower.includes("individual")) return "individual";
  return "unknown";
}

type ModalState =
  | { type: "extend_trial"; sub: Record<string, unknown> }
  | { type: "cancel"; sub: Record<string, unknown> }
  | { type: "refund"; sub: Record<string, unknown> }
  | { type: "upgrade"; sub: Record<string, unknown> }
  | { type: "invoices"; sub: Record<string, unknown> }
  | null;

export default function Subscriptions() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";
  const qc = useQueryClient();

  const [tab, setTab] = useState<"all" | "failed">("all");
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  const [trialDays, setTrialDays] = useState(7);
  const [cancelReason, setCancelReason] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [upgradePlan, setUpgradePlan] = useState("strata");

  // Queries
  const subsQuery = useQuery({
    queryKey: ["admin", "subscriptions", planFilter, statusFilter],
    queryFn: () =>
      getSubscriptions({
        plan: planFilter !== "all" ? planFilter : undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
      }),
  });

  const failedQuery = useQuery({
    queryKey: ["admin", "subscriptions", "past_due"],
    queryFn: () => getSubscriptions({ status: "past_due" }),
  });

  const statsQuery = useQuery({
    queryKey: ["admin", "dashboard-stats"],
    queryFn: getDashboardStats,
  });

  const stats = statsQuery.data?.data;
  const allSubs: Record<string, unknown>[] = subsQuery.data?.data ?? [];
  const failedSubs: Record<string, unknown>[] = failedQuery.data?.data ?? [];
  const displaySubs = tab === "failed" ? failedSubs : allSubs;

  // Compute MRR
  const activeSubs = allSubs.filter((s) => s.status === "active" || s.status === "trialing");
  const mrr = activeSubs.reduce((acc: number, s) => {
    const key = getPlanKey((s.price_id as string) ?? "");
    return acc + (PLAN_PRICES[key] ?? 0);
  }, 0);

  const mrrByPlan: Record<string, number> = {};
  activeSubs.forEach((s) => {
    const key = getPlanKey((s.price_id as string) ?? "");
    mrrByPlan[key] = (mrrByPlan[key] ?? 0) + (PLAN_PRICES[key] ?? 0);
  });

  const churnCount = allSubs.filter((s) => s.status === "canceled").length;
  const newThisMonth = allSubs.filter((s) => {
    const start = new Date();
    start.setDate(1);
    return s.created_at && new Date(s.created_at as string) >= start;
  }).length;

  // Mutations
  const extendMutation = useMutation({
    mutationFn: ({ userId, days }: { userId: string; days: number }) =>
      extendTrial(userId, days, actorId),
    onSuccess: () => {
      toast.success("Trial extended");
      qc.invalidateQueries({ queryKey: ["admin", "subscriptions"] });
      setModal(null);
    },
    onError: () => toast.error("Failed to extend trial"),
  });

  const cancelMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      cancelSubscription(userId, reason, actorId),
    onSuccess: () => {
      toast.success("Subscription cancelled");
      qc.invalidateQueries({ queryKey: ["admin", "subscriptions"] });
      setModal(null);
    },
    onError: () => toast.error("Failed to cancel subscription"),
  });

  const upgradeMutation = useMutation({
    mutationFn: ({ userId, plan }: { userId: string; plan: string }) =>
      upgradeSubscription(userId, plan, actorId),
    onSuccess: () => {
      toast.success("Subscription updated");
      qc.invalidateQueries({ queryKey: ["admin", "subscriptions"] });
      setModal(null);
    },
    onError: () => toast.error("Failed to update subscription"),
  });

  const markPaidMutation = useMutation({
    mutationFn: (subId: string) => markAsPaid(subId, actorId),
    onSuccess: () => {
      toast.success("Marked as paid");
      qc.invalidateQueries({ queryKey: ["admin", "subscriptions"] });
    },
    onError: () => toast.error("Failed to mark as paid"),
  });

  const refundMutation = useMutation({
    mutationFn: ({ customerId, reason }: { customerId: string; reason: string }) =>
      refundPayment(customerId, reason, actorId),
    onSuccess: () => {
      toast.success("Refund initiated");
      qc.invalidateQueries({ queryKey: ["admin", "subscriptions"] });
      setModal(null);
    },
    onError: () => toast.error("Failed to initiate refund"),
  });

  const columns: DataTableColumn<Record<string, unknown>>[] = [
    {
      key: "user",
      label: "User",
      render: (row) => {
        const profile = row.profile as Record<string, unknown> | undefined;
        return (
          <div>
            <p className="font-medium text-gray-900 text-sm">
              <span className="truncate max-w-[160px] block">
                {(profile?.full_name as string) ?? "—"}
              </span>
            </p>
            <p className="text-xs text-gray-400">
              <span className="truncate max-w-[160px] block">
                {(profile?.email as string) ?? "—"}
              </span>
            </p>
          </div>
        );
      },
    },
    {
      key: "plan",
      label: "Plan",
      render: (row) => {
        const key = getPlanKey((row.price_id as string) ?? "");
        return (
          <Badge variant="outline" className="text-xs whitespace-nowrap">
            {PLAN_LABELS[key] ?? key}
          </Badge>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => (
        <span className="whitespace-nowrap">
          <StatusBadge status={row.status as string} />
        </span>
      ),
    },
    {
      key: "mrr",
      label: "MRR",
      sortable: true,
      render: (row) => {
        const key = getPlanKey((row.price_id as string) ?? "");
        const price = PLAN_PRICES[key];
        return (
          <span className="tabular-nums text-sm font-medium whitespace-nowrap">
            {price != null ? `CA$${price.toFixed(2)}` : "—"}
          </span>
        );
      },
    },
    {
      key: "current_period_end",
      label: "Next Billing",
      render: (row) =>
        row.current_period_end ? (
          <span className="text-sm tabular-nums whitespace-nowrap">
            {new Date(row.current_period_end as string).toLocaleDateString("en-CA")}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      key: "payment_method",
      label: "Payment Method",
      render: (row) => (
        <span className="text-xs text-gray-500 whitespace-nowrap">
          {row.payment_method_brand
            ? `${row.payment_method_brand} ****${(row.payment_method_last4 as string) ?? ""}`
            : "—"}
        </span>
      ),
    },
    {
      key: "stripe_customer_id",
      label: "Stripe ID",
      render: (row) => (
        <span className="text-xs font-mono text-gray-400 truncate max-w-[100px] block">
          {(row.stripe_customer_id as string) ?? "—"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() =>
              setExpandedRow(expandedRow === (row.id as string) ? null : (row.id as string))
            }
            aria-label="Toggle row detail"
          >
            {expandedRow === (row.id as string) ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Row actions">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => setExpandedRow(expandedRow === (row.id as string) ? null : (row.id as string))}
              >
                View detail
              </DropdownMenuItem>
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => { setModal({ type: "upgrade", sub: row }); setUpgradePlan("strata"); }}
              >
                Upgrade / downgrade
              </DropdownMenuItem>
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => { setModal({ type: "extend_trial", sub: row }); setTrialDays(7); }}
              >
                Extend trial
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => markPaidMutation.mutate(row.id as string)}
              >
                Mark as paid
              </DropdownMenuItem>
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => setModal({ type: "invoices", sub: row })}
              >
                View invoices
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { setModal({ type: "refund", sub: row }); setRefundReason(""); }}
                className="text-amber-600 whitespace-nowrap"
              >
                Refund last payment
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => { setModal({ type: "cancel", sub: row }); setCancelReason(""); }}
                className="text-red-600 whitespace-nowrap"
              >
                Cancel subscription
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const filters = (
    <div className="flex items-center gap-2">
      <Select value={planFilter} onValueChange={setPlanFilter}>
        <SelectTrigger className="h-9 w-[160px] text-sm">
          <SelectValue placeholder="All Plans" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Plans</SelectItem>
          <SelectItem value="individual">Individual</SelectItem>
          <SelectItem value="strata">Strata Council</SelectItem>
          <SelectItem value="property_manager">Property Manager</SelectItem>
        </SelectContent>
      </Select>
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="h-9 w-[140px] text-sm">
          <SelectValue placeholder="All Statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="past_due">Past Due</SelectItem>
          <SelectItem value="canceled">Cancelled</SelectItem>
          <SelectItem value="trialing">Trialling</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total MRR"
          value={statsQuery.isLoading ? "—" : `CA$${mrr.toFixed(2)}`}
          icon={<DollarSign className="h-5 w-5" />}
          color="green"
          loading={statsQuery.isLoading}
        />
        <StatCard
          title="MRR by Plan"
          value={
            statsQuery.isLoading
              ? "—"
              : Object.entries(mrrByPlan)
                  .map(([k, v]) => `${PLAN_LABELS[k] ?? k}: CA$${v.toFixed(2)}`)
                  .join(" · ") || "—"
          }
          icon={<DollarSign className="h-5 w-5" />}
          color="blue"
          loading={statsQuery.isLoading}
        />
        <StatCard
          title="Churn This Month"
          value={churnCount}
          icon={<TrendingDown className="h-5 w-5" />}
          color="red"
          loading={subsQuery.isLoading}
        />
        <StatCard
          title="New Subscribers"
          value={newThisMonth}
          changeLabel="this month"
          icon={<Users className="h-5 w-5" />}
          color="purple"
          loading={subsQuery.isLoading}
        />
      </div>

      {/* Failed payments alert */}
      {failedSubs.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-800">
              {failedSubs.length} subscription{failedSubs.length > 1 ? "s" : ""} with failed payments
            </p>
            <p className="text-xs text-red-600">These accounts are past due and may lose access soon.</p>
          </div>
          <Badge className="bg-red-600 text-white text-xs shrink-0 whitespace-nowrap">Requires attention</Badge>
          <Button
            size="sm"
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50 shrink-0 whitespace-nowrap"
            onClick={() => setTab("failed")}
          >
            View {failedSubs.length} failed
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as "all" | "failed")}>
        <TabsList>
          <TabsTrigger value="all" className="whitespace-nowrap">
            All Subscriptions
            <Badge variant="secondary" className="ml-2 text-xs">{allSubs.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="failed" className="whitespace-nowrap">
            Failed Payments
            {failedSubs.length > 0 && (
              <Badge className="ml-2 text-xs bg-red-100 text-red-700 border-red-200">{failedSubs.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <DataTable
            columns={columns}
            data={displaySubs.flatMap((sub) => {
              const rows: Record<string, unknown>[] = [sub];
              if (expandedRow === (sub.id as string)) {
                rows.push({ _expanded: true, _parentId: sub.id, id: `${sub.id}_detail`, ...sub });
              }
              return rows;
            })}
            loading={subsQuery.isLoading}
            filters={filters}
            emptyMessage="No subscriptions found."
          />
        </TabsContent>

        <TabsContent value="failed" className="mt-4">
          <DataTable
            columns={columns}
            data={failedSubs}
            loading={failedQuery.isLoading}
            emptyMessage="No failed payments."
          />
        </TabsContent>
      </Tabs>

      {/* Extend Trial Dialog */}
      <Dialog open={modal?.type === "extend_trial"} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Extend Trial</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-600">
              Extending trial for{" "}
              <span className="font-semibold">
                {((modal as { type: "extend_trial"; sub: Record<string, unknown> } | null)?.sub?.profile as Record<string, unknown> | undefined)?.full_name as string ?? "user"}
              </span>
            </p>
            <div className="space-y-1">
              <Label htmlFor="trial-days" className="whitespace-nowrap">Days to extend</Label>
              <Input
                id="trial-days"
                type="number"
                min={1}
                max={90}
                value={trialDays}
                onChange={(e) => setTrialDays(Number(e.target.value))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="whitespace-nowrap" onClick={() => setModal(null)}>Cancel</Button>
            <Button
              className="whitespace-nowrap"
              onClick={() => {
                if (modal?.type === "extend_trial")
                  extendMutation.mutate({ userId: modal.sub.user_id as string, days: trialDays });
              }}
              disabled={extendMutation.isPending}
            >
              {extendMutation.isPending ? "Extending…" : "Extend Trial"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={modal?.type === "cancel"} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2">
              <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
              <p className="text-sm text-red-700">This will immediately cancel the subscription.</p>
            </div>
            <p className="text-sm text-gray-600">
              Cancelling subscription for{" "}
              <span className="font-semibold">
                {((modal as { type: "cancel"; sub: Record<string, unknown> } | null)?.sub?.profile as Record<string, unknown> | undefined)?.full_name as string ?? "user"}
              </span>
            </p>
            <div className="space-y-1">
              <Label htmlFor="cancel-reason" className="whitespace-nowrap">Reason</Label>
              <Textarea
                id="cancel-reason"
                placeholder="Reason for cancellation…"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="whitespace-nowrap" onClick={() => setModal(null)}>Cancel</Button>
            <Button
              variant="destructive"
              className="whitespace-nowrap"
              onClick={() => {
                if (modal?.type === "cancel")
                  cancelMutation.mutate({ userId: modal.sub.user_id as string, reason: cancelReason });
              }}
              disabled={cancelMutation.isPending || !cancelReason.trim()}
            >
              {cancelMutation.isPending ? "Cancelling…" : "Cancel Subscription"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Modal */}
      <ConfirmModal
        open={modal?.type === "refund"}
        onClose={() => setModal(null)}
        onConfirm={() => {
          if (modal?.type === "refund")
            refundMutation.mutate({
              customerId: (modal.sub.stripe_customer_id as string) ?? (modal.sub.user_id as string),
              reason: refundReason,
            });
        }}
        title="Refund Last Payment"
        description={`Refund last payment for ${((modal as { type: "refund"; sub: Record<string, unknown> } | null)?.sub?.profile as Record<string, unknown> | undefined)?.full_name as string ?? "user"}. This action cannot be undone.`}
        requireTyping
        loading={refundMutation.isPending}
        variant="destructive"
      />

      {/* Upgrade Dialog */}
      <Dialog open={modal?.type === "upgrade"} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Plan</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-600">
              Change plan for{" "}
              <span className="font-semibold">
                {((modal as { type: "upgrade"; sub: Record<string, unknown> } | null)?.sub?.profile as Record<string, unknown> | undefined)?.full_name as string ?? "user"}
              </span>
            </p>
            <div className="space-y-1">
              <Label htmlFor="new-plan" className="whitespace-nowrap">New plan</Label>
              <Select value={upgradePlan} onValueChange={setUpgradePlan}>
                <SelectTrigger id="new-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual — CA$9.99/mo</SelectItem>
                  <SelectItem value="strata">Strata Council — CA$18.99/mo</SelectItem>
                  <SelectItem value="property_manager">Property Manager — CA$28.99/mo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="whitespace-nowrap" onClick={() => setModal(null)}>Cancel</Button>
            <Button
              className="whitespace-nowrap"
              onClick={() => {
                if (modal?.type === "upgrade")
                  upgradeMutation.mutate({ userId: modal.sub.user_id as string, plan: upgradePlan });
              }}
              disabled={upgradeMutation.isPending}
            >
              {upgradeMutation.isPending ? "Updating…" : "Update Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoices Dialog (placeholder — real invoices come from Stripe) */}
      <Dialog open={modal?.type === "invoices"} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invoices</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center text-sm text-gray-500 space-y-2">
            <p>
              Stripe customer ID:{" "}
              <span className="font-mono">
                {((modal as { type: "invoices"; sub: Record<string, unknown> } | null)?.sub?.stripe_customer_id as string) ?? "N/A"}
              </span>
            </p>
            <p className="text-xs text-gray-400">
              Open the Stripe dashboard to view full invoice history for this customer.
            </p>
            {(modal as { type: "invoices"; sub: Record<string, unknown> } | null)?.sub?.stripe_customer_id && (
              <Button
                variant="outline"
                size="sm"
                className="whitespace-nowrap"
                onClick={() =>
                  window.open(
                    `https://dashboard.stripe.com/customers/${(modal as { type: "invoices"; sub: Record<string, unknown> }).sub.stripe_customer_id}`,
                    "_blank"
                  )
                }
              >
                Open in Stripe
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="whitespace-nowrap" onClick={() => setModal(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
